import * as React from 'react';

import { prefersReducedMotion } from '../../utils/prefers-reduced-motion';
import { STEP_SETTLE_EASING, STEP_SETTLE_MS } from './questionnaire-tokens';

/**
 * Carries each incoming step in, and rewinds the answer column's scroll.
 *
 * Returns a callback ref to put on the element wrapping the step body. The
 * animation re-runs whenever `stepKey` changes, so the key must identify the
 * step rather than its contents — a key that changes as the reader types would
 * restart the carry-in on every keystroke.
 *
 * A callback ref rather than an object ref: the shell renders its body inside a
 * portal, so the node is attached after layout effects have already run and a
 * `useRef` object would still read null.
 */
export const useStepTransition = (stepKey: string) => {
    const [node, setNode] = React.useState<HTMLElement | null>(null);
    const isFirstStep = React.useRef(true);

    React.useEffect(() => {
        if (!node) return;

        node.scrollTop = 0;

        // The opening step arrives with the surface itself, which already has
        // its own enter animation. Carrying it in as well double-animates it.
        if (isFirstStep.current) {
            isFirstStep.current = false;
            return;
        }

        if (prefersReducedMotion()) return;

        // jsdom has no Web Animations API, so tests would throw here.
        if (typeof node.animate !== 'function') return;

        const animation = node.animate(
            [
                { opacity: 0.55, transform: 'translateY(6px)' },
                { opacity: 1, transform: 'translateY(0)' },
            ],
            { duration: STEP_SETTLE_MS, easing: STEP_SETTLE_EASING },
        );

        return () => animation.cancel();
    }, [node, stepKey]);

    return setNode;
};
